import { isFulfilled } from '@reduxjs/toolkit'
import { useConfirm } from 'legacy/lut/components/shared/Confirm/useConfirm'
import { history } from 'legacy/lut/helpers/route'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { initFormData } from 'cng-common/features/form'
import { getUrlWithParams } from 'core-components/helpers/url'
import { Logic, Operator } from 'core-components/types'
import {
  changeQBRLastStep,
  changeUnpopulatedMessages,
  requestApplyChanges,
  requestResetData,
  selectCurrencyOptions,
  selectNetNdpOptions,
  selectQBRDocumentFormData,
  selectQBRDocumentGlobalPartnerId,
  selectQBREntryId,
  selectQbrIsApplyingChanges,
  selectQbrIsLegacyDocument,
  selectQBRLastStep,
  selectQBRPartners,
  selectQBRRtm,
} from 'partner360/features/QBR'
import { requestFinalizeDocumentWithoutSavingChanges } from 'partner360/features/QBR/api/requestFinalizeDocumentWithoutSavingChanges'
import { mapToPeriodOption } from 'partner360/features/QBR/helpers/mapToPeriodOption'
import { selectQbrCurrentFQuarter } from 'partner360/features/QBR/selectors/selectQbrCurrentFQuarter'
import { selectQbrCurrentFYear } from 'partner360/features/QBR/selectors/selectQbrCurrentFYear'
import { selectQbrPeriodOptions } from 'partner360/features/QBR/selectors/selectQbrPeriodOptions'

import { TRANSLATION_KEYS } from '../../../translations'
import { QBR_DOCUMENT_SAVE_AS_CONFIRM_IDENTIFIER } from '../components/qbrDocumentSaveAsConfirm'
import { QBR_DOCUMENT_SAVE_CONFIRM_IDENTIFIER } from '../components/qbrDocumentSaveConfirm'
import { getJbpQbrAttachments } from '../components/steps/exportAndAttachments/components/qbrAttachments/helpers/getJbpQbrAttachments'
import {
  QBR_SETTINGS_FORM_KEY,
  selectIsQbrChanged,
} from '../components/steps/settingsStep/components/qbrSettingsForm'
import { selectQbrSettingsForm } from '../components/steps/settingsStep/components/qbrSettingsForm/selectors/selectQbrSettingsForm'
import { ICheckAndSaveFn, ISaveFn } from '../models/iUseSave'

export const useQbrSave = () => {
  /* ------------------ State ------------------ */
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [isPartnerHasAttachments, setIsPartnerHasAttachments] = useState(false)
  /* ------------------ Selectors ------------------ */
  const currentFQuarter = useSelector(selectQbrCurrentFQuarter)
  const currentFYear = useSelector(selectQbrCurrentFYear)
  const periodOptions = useSelector(selectQbrPeriodOptions)
  const documentEntryId = useSelector(selectQBREntryId)
  const qbrPartners = useSelector(selectQBRPartners)
  const qbrRtm = useSelector(selectQBRRtm)
  const netNdpOptions = useSelector(selectNetNdpOptions)
  const currencyOptions = useSelector(selectCurrencyOptions)
  const document = useSelector(selectQBRDocumentFormData)
  const isLegacyDocument = useSelector(selectQbrIsLegacyDocument)
  const currentStep = useSelector(selectQBRLastStep)
  const isSettingsWasUpdated = useSelector(selectIsQbrChanged)
  const settingsForm = useSelector(selectQbrSettingsForm)
  const isApplyingChanges = useSelector(selectQbrIsApplyingChanges)
  const globalPartnerId = useSelector(selectQBRDocumentGlobalPartnerId)

  /* ------------------ Custom Hooks ------------------ */
  const dispatch = useAppDispatch()
  const { confirm } = useConfirm()

  /* ------------------ Constants ------------------ */
  const attachmentsCrud = getJbpQbrAttachments()

  const isNameInputEmpty = useMemo(() => {
    if (!settingsForm) {
      return false
    }

    return settingsForm.formData.name.length === 0
  }, [settingsForm])

  const isOfferingsEmpty = useMemo(() => {
    if (!settingsForm) {
      return false
    }

    return settingsForm.formData.offerings.length === 0
  }, [settingsForm])

  const isPartnersEmpty = useMemo(() => {
    return qbrPartners.length === 0
  }, [qbrPartners])

  const isNotFullFilled = useMemo(() => {
    return isNameInputEmpty || isOfferingsEmpty || isPartnersEmpty
  }, [isNameInputEmpty, isOfferingsEmpty, isPartnersEmpty])

  /* ------------------ Methods ------------------ */
  const showUnpopulatedBlock = useCallback(() => {
    const messages: string[] = []

    if (isNameInputEmpty) {
      messages.push(TRANSLATION_KEYS.unpopulatedDataBlock.nameInputEmpty)
    }

    if (isOfferingsEmpty) {
      messages.push(TRANSLATION_KEYS.unpopulatedDataBlock.offeringsEmpty)
    }

    if (isPartnersEmpty) {
      messages.push(TRANSLATION_KEYS.unpopulatedDataBlock.partnersEmpty)
    }

    dispatch(changeUnpopulatedMessages(messages))
  }, [dispatch, isNameInputEmpty, isOfferingsEmpty, isPartnersEmpty])

  const onReset = useCallback(async () => {
    if (documentEntryId) {
      dispatch(requestResetData(documentEntryId))
    }
  }, [dispatch, documentEntryId])

  const save = useCallback<ISaveFn>(
    async ({ isFinalized, shouldSaveAsNew }) => {
      if (!settingsForm) {
        return false
      }

      dispatch(changeUnpopulatedMessages([]))

      const action = await dispatch(
        requestApplyChanges({
          globalPartnerId: globalPartnerId,
          entryId: shouldSaveAsNew ? null : documentEntryId,
          settings: {
            NetNdp: netNdpOptions,
            Currency: currencyOptions,
            SelectedNetNdp: settingsForm.formData.netNdp,
            SelectedCurrency: settingsForm.formData.currency,
            SelectedOfferings: settingsForm.formData.offerings,
            SelectedRounding: settingsForm.formData.rounding,
            Quarters: {
              Current: {
                FYear: currentFYear,
                FQuarter: currentFQuarter,
              },
              Selected: [
                {
                  FYear: settingsForm.formData.selectedPeriodFYear,
                  FQuarters: settingsForm.formData.selectedPeriodFQuarters.map((FQuarter) => ({
                    FQuarter,
                  })),
                },
              ],
              Available: periodOptions.map(mapToPeriodOption),
            },
            ExportSlides: settingsForm.formData.exportSlides,
            ELTVersion: settingsForm.formData.eltVersion,
            IsGlobalPlans: settingsForm.formData.isGlobalPlans,
          },
          partners: qbrPartners.map((el) => el.PartnerID),
          rtm: qbrRtm,
          name: settingsForm.formData.name,
          document: document,
          isLegacyDocument,
          isFinalized,
        })
      )

      if (isFulfilled(action)) {
        dispatch(
          initFormData({
            formKey: QBR_SETTINGS_FORM_KEY,
            formData: settingsForm.formData,
          })
        )
        history.push(getUrlWithParams({ ['documentId']: action.payload.EntryId }))
        return true
      } else {
        setShowErrorModal(true)
        return false
      }
    },
    [
      currencyOptions,
      currentFQuarter,
      currentFYear,
      dispatch,
      document,
      documentEntryId,
      globalPartnerId,
      isLegacyDocument,
      netNdpOptions,
      periodOptions,
      qbrPartners,
      qbrRtm,
      settingsForm,
    ]
  )

  const onCloseErrorModal = useCallback(() => {
    setShowErrorModal(false)
  }, [])

  const saveAsNew = useCallback(async () => {
    if (isPartnerHasAttachments) {
      const shouldSaveAsNew = await confirm(QBR_DOCUMENT_SAVE_AS_CONFIRM_IDENTIFIER)
      if (!shouldSaveAsNew) {
        return
      }
    }
    await save({ shouldSaveAsNew: true })
  }, [confirm, isPartnerHasAttachments, save])

  const checkAndSave = useCallback<ICheckAndSaveFn>(
    async ({ shouldShowConfirmationDialog, isFinalized, shouldKeepCurrentStep }) => {
      if (isNotFullFilled) {
        showUnpopulatedBlock()
        return false
      }

      if (shouldShowConfirmationDialog) {
        const shouldSave = await confirm(QBR_DOCUMENT_SAVE_CONFIRM_IDENTIFIER)
        if (!shouldSave) {
          return false
        }
      }
      if (!shouldKeepCurrentStep) {
        dispatch(changeQBRLastStep(1))
      }
      return await save({ isFinalized })
    },
    [confirm, dispatch, isNotFullFilled, save, showUnpopulatedBlock]
  )

  const onNextButtonClick = useCallback(async () => {
    const isSavedSuccessful = await checkAndSave({
      shouldShowConfirmationDialog: !isNotFullFilled && isSettingsWasUpdated,
    })
    if (!isSavedSuccessful) {
      return
    }

    if (!isNotFullFilled) {
      dispatch(changeQBRLastStep(currentStep + 1))
    }
  }, [currentStep, dispatch, isNotFullFilled, checkAndSave, isSettingsWasUpdated])

  const finalizeWithoutSaving = useCallback(() => {
    if (documentEntryId) {
      dispatch(changeQBRLastStep(1))
      dispatch(requestFinalizeDocumentWithoutSavingChanges(documentEntryId))
    }
  }, [documentEntryId, dispatch])

  const finalizeWithSaving = useCallback(async () => {
    await checkAndSave({ shouldShowConfirmationDialog: Boolean(document), isFinalized: true })
  }, [checkAndSave, document])

  useEffect(() => {
    attachmentsCrud.setFilter({
      logic: Logic.AND,
      filters: [
        {
          value: Number(documentEntryId),
          operator: Operator.EQUAL,
          field: 'DocumentId',
        },
      ],
    })
    attachmentsCrud.fetch().then((response) => {
      if (response.items.length > 0) {
        setIsPartnerHasAttachments(true)
        return
      }
    })
    return () => {
      setIsPartnerHasAttachments(false)
    }
  }, [documentEntryId, attachmentsCrud])

  /* ------------------ Result ------------------ */
  return {
    isSavingProgress: isApplyingChanges,
    saveAsNew,
    checkAndSave,
    onNextButtonClick,
    isNotFullFilled,
    onReset,
    onCloseErrorModal,
    showErrorModal,
    finalizeWithoutSaving,
    finalizeWithSaving,
    setIsPartnerHasAttachments,
  }
}
